/**
 * Canonical LS / Xero "sale link" cells for every sales table.
 *
 * One place owns the row -> `InvoiceLinkCell` field mapping so a field rename
 * or a display rule (e.g. the clean Xero title lives in the Reference field,
 * not InvoiceNumber) lands once instead of being re-derived per table.
 *
 * Consumers: the sales exception configs (`compareExceptions`,
 * `simpleExceptions`, `surchargeExceptions`) and the Data Explorer sales view
 * (`config/dataExplorer/salesViewConfig`). All four read the same sales row
 * shape (`ls_id` / `invoice_number` / `lightspeed_sale_link` /
 * `accounts_transaction_id` / `xero_reference` / `xero_invoice_link` /
 * `xero_type`). Callers vary only by `showIcons` / `layout`.
 */
import React from 'react'
import { Typography } from '@mui/material'
import InvoiceLinkCell from '../../../components/shared/InvoiceLinkCell'

export interface SaleLinkOptions {
  showIcons?: boolean
  layout?: 'vertical' | 'horizontal'
}

/** LS receipt cell - icon + receipt link + kebab (sale history / ledger). */
export const renderSaleLsLink = (
  row: Record<string, any>,
  { showIcons = true, layout = 'horizontal' }: SaleLinkOptions = {},
): React.ReactNode =>
  React.createElement(InvoiceLinkCell, {
    lightspeed: {
      id: String(row.ls_id ?? row.sale_id ?? row.object_id ?? ''),
      invoiceNumber: row.invoice_number,
      saleLink: row.lightspeed_sale_link,
      ledgerLink: row.lightspeed_ledger_link,
    },
    showIcons,
    layout,
  })

/**
 * Xero invoice / credit-note cell. The clean short title lives in the Xero
 * Reference field - some FPs put a long descriptor in the Xero InvoiceNumber
 * ("Service Outlet Register 1 - INV-ABY-5448") and the bare number in
 * Reference - so prefer Reference for invoices and fall back to the number
 * only on credit notes. Returns "-" when the sale has no Xero document.
 */
export const renderSaleXeroLink = (
  row: Record<string, any>,
  { showIcons = true, layout = 'horizontal' }: SaleLinkOptions = {},
): React.ReactNode => {
  if (!row.xero_invoice_link) {
    return React.createElement(
      Typography,
      { variant: 'caption', color: 'text.secondary' },
      '-',
    )
  }
  return React.createElement(InvoiceLinkCell, {
    xero: {
      id: row.xero_invoice_id ?? row.accounts_transaction_id,
      reference:
        row.xero_type === 'credit_note'
          ? row.xero_reference || row.xero_invoice_number || 'N/A'
          : row.xero_reference || 'N/A',
      invoiceLink: row.xero_invoice_link,
      type: row.xero_type,
    },
    showIcons,
    layout,
  })
}
